// Flat-shaded material factory — replaces the cel toon shader. Keeps the same
// signature as the old makeToonMaterial so call sites are drop-in compatible.
// `bands` and `rim` are accepted for compatibility but ignored (lambert+flat
// shading already produces the faceted Rock-Crossing look).

import * as THREE from 'three';

const registry = []; // for day/night light updates

export function makeToonMaterial({
  color = 0x445566,
  bands = 3,           // ignored — kept for API compat
  rim = 0.5,           // ignored — kept for API compat
  emissive = 0x000000,
  emissiveIntensity = 0,
} = {}) {
  void bands; void rim;
  const m = new THREE.MeshStandardMaterial({
    color,
    flatShading: true,
    roughness: 1.0,
    metalness: 0.0,
    emissive,
    emissiveIntensity,
  });
  registry.push(m);
  return m;
}

// Inverted-hull outline pass — kept identical to toon.js's API.
export function makeOutlineMaterial({ thickness = 0.05, color = 0x000000 } = {}) {
  return new THREE.ShaderMaterial({
    uniforms: { uThickness: { value: thickness }, uColor: { value: new THREE.Color(color) } },
    vertexShader: `
      uniform float uThickness;
      void main() {
        vec3 pos = position + normal * uThickness;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 uColor;
      void main() { gl_FragColor = vec4(uColor, 1.0); }
    `,
    side: THREE.BackSide,
    depthWrite: true,
  });
}

export function addOutline(mesh, opts = {}) {
  const thickness = typeof opts === 'number' ? opts : (opts.thickness ?? 0.05);
  const outline = new THREE.Mesh(mesh.geometry, makeOutlineMaterial({ thickness }));
  outline.scale.copy(mesh.scale);
  mesh.add(outline);
  return outline;
}

// Called by the day/night cycle each frame so all materials share lighting.
// Now that we use MeshStandardMaterial, the scene's directional + ambient
// lights drive shading directly. This hook stays as a no-op so cycle.js does
// not break, and remains a place to retune palette via material tweaks later.
export function updateAllToonMaterials(_lightDir, _lightColor, _ambient) {
  // intentionally empty — standard materials respond to scene lights
}

// Alias used by cycle.js — kept for API compat, no-op since MeshStandardMaterial
// responds to scene lights directly.
export function updateToonLighting(_opts) {
  // intentionally empty — standard materials respond to scene lights
}
